import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { BreadcrumbDefinition } from 'xng-breadcrumb/lib/types';
import { GoliathDocumentsEnumerationTypeStore } from '../enumeration-types';
import { ParametersComponent } from './parameters.component';
import {
  ProductionFeatureDefinition,
  ServiceWorkOrderFeatureDefinition,
} from '@digiwall/goliath-products/config';
import { ENUMERATION_TYPE_STORE } from '@digiwall/core';
import { ClassificationTypeCategory } from '@digiwall/goliath-core/proxy';
import { GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';

// Merged "Operations" settings screen. A single ParametersComponent host at
// /operations/config whose left menu groups the former Production, Service
// Work Order and Planning settings into sections. Child paths are namespaced
// (production/*, service-work-order/* and planning/*) so the areas don't
// collide; the two feature areas are each gated by their own feature, while
// planning is gated per-item by permission. The host is an empty path, so
// DwSectionGroupingRoutingComponent lands on the first *enabled* section's
// first item (robust when only one feature is active).
const routes: Routes = [
  {
    path: 'operations/config',
    data: {
      breadcrumb: {
        skip: true,
      } as BreadcrumbDefinition,
    },
    children: [
      {
        path: '',
        component: ParametersComponent,
        data: {
          category: 'operations',
          breadcrumb: {
            label: '::Menu:Settings:Operations',
            skip: true,
          } as BreadcrumbDefinition,
        },
        children: [
          // General (always available — not gated by a feature)
          {
            path: 'general/enumeration-types',
            providers: [
              {
                provide: ENUMERATION_TYPE_STORE,
                useExisting: GoliathDocumentsEnumerationTypeStore,
              },
            ],
            loadChildren: () =>
              import('@digiwall/core-features').then(m => m.EnumerationTypeModule),
          },
          // Production
          {
            path: 'production/work-order-day-books',
            title: 'Doc::WorkOrderDayBooks',
            canMatch: [ProductionFeatureDefinition],
            loadChildren: () =>
              import('./work-order-day-books/work-order-day-books.module').then(
                m => m.WorkOrderDayBooksModule,
              ),
          },
          {
            path: 'production/checklists',
            title: 'GoliathCore::Menu:Settings:Checklists',
            canMatch: [ProductionFeatureDefinition],
            loadChildren: () =>
              import('@digiwall/goliath-products').then(m => m.ProductionCheckListModule),
          },
          {
            path: 'production/enumeration-types',
            canMatch: [ProductionFeatureDefinition],
            providers: [
              {
                provide: ENUMERATION_TYPE_STORE,
                useExisting: GoliathDocumentsEnumerationTypeStore,
              },
            ],
            loadChildren: () =>
              import('@digiwall/core-features').then(m => m.EnumerationTypeModule),
          },
          {
            path: 'production/classifications',
            title: 'GoliathCore::Company:ProductionClassifications',
            canMatch: [ProductionFeatureDefinition],
            data: {
              category: ClassificationTypeCategory.Production,
            },
            loadChildren: () =>
              import('@digiwall/goliath-core').then(m => m.ClassificationValuesModule),
          },
          // Service work order
          {
            path: 'service-work-order/work-order-day-books',
            title: 'Doc::ServiceWorkOrderDoc:WorkOrderDayBooks',
            canMatch: [ServiceWorkOrderFeatureDefinition],
            loadChildren: () =>
              import('./work-order-day-books/service-work-order-day-books.module').then(
                m => m.ServiceWorkOrderDayBooksModule,
              ),
          },
          {
            path: 'service-work-order/enumeration-types',
            canMatch: [ServiceWorkOrderFeatureDefinition],
            providers: [
              {
                provide: ENUMERATION_TYPE_STORE,
                useExisting: GoliathDocumentsEnumerationTypeStore,
              },
            ],
            loadChildren: () =>
              import('@digiwall/core-features').then(m => m.EnumerationTypeModule),
          },
          {
            path: 'service-work-order/core-enumeration-types',
            canMatch: [ServiceWorkOrderFeatureDefinition],
            providers: [
              {
                provide: ENUMERATION_TYPE_STORE,
                useExisting: GoliathCoreEnumerationTypeStore,
              },
            ],
            loadChildren: () =>
              import('@digiwall/core-features').then(m => m.EnumerationTypeModule),
          },
          // Planning
          {
            path: 'planning/configurations',
            title: 'Doc::Menu:Settings:PlanningConfigurations',
            loadChildren: () =>
              import('../planning-configuration/planning-configuration.module').then(
                m => m.PlanningConfigurationModule,
              ),
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class GoliathOperationsManagementRoutingModule {}
